
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    rto_name: string;
    renewal_date: string;
    expiry_date: string;
    amount: string;
    rc_document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditRcRenewal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [docPreview, setDocPreview] = useState<string | null>(null);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");
    const { hasPermission } = useAuth();
    const { config } = useConfig();
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedRenewalDate = watch("renewal_date");
    const watchedExpiryDate = watch("expiry_date");

    // Pending check
    useEffect(() => {
        if (!selectedVehicle || !location.state?.id) { setSubmissionBlocked(false); setPendingMessage(""); return; }
        const checkPending = async () => {
            try {
                const response: any = await apiPost("rc_renewal/check_pending", { vehicle_id: selectedVehicle, exclude_id: location.state.id });
                if (response?.exists) { setPendingMessage(response.message); setSubmissionBlocked(true); }
                else { setSubmissionBlocked(false); setPendingMessage(""); }
            } catch { setSubmissionBlocked(false); setPendingMessage(""); }
        };
        checkPending();
    }, [selectedVehicle, location.state?.id]);

    // Fetch Branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchRes: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };
        fetchVehicles();
    }, [selectedBranch]);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            if (!location.state || !location.state.id) {
                toast.error("No Renewal ID found");
                navigate(`${process.env.PUBLIC_URL}/rc_renewal`);
                return;
            }

            try {
                const result = await apiPost("rc_renewal/viewbyid", { id: location.state.id });

                setValue("branch_id", result.branch_id);

                // Manually fetch vehicles for this branch to ensure they are loaded
                const vehiclesData: any = await apiPost("vehicles/list", { branch_id: result.branch_id });
                if (Array.isArray(vehiclesData)) {
                    const activeVehicles = vehiclesData.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (vehiclesData.data) {
                    const activeVehicles = Array.isArray(vehiclesData.data) ? vehiclesData.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                }

                setValue("vehicle_id", result.vehicle_id);
                setValue("rto_name", result.rto_name);
                setValue("renewal_date", result.renewal_date);
                setValue("expiry_date", result.expiry_date);
                setValue("amount", result.amount);
                setValue("remarks", result.remarks);

                if (result.rc_document) {
                    setDocPreview(`${mediaUrl}${result.rc_document}`);
                }

                setLoading(false);
            } catch (error) {
                console.error(error);
                toast.error("Failed to fetch details");
                setLoading(false);
            }
        };
        fetchData();
    }, [location.state, navigate, setValue]);

    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "A pending RC renewal request already exists for this vehicle.");
            return;
        }

        setLoading(true);
        try {
            let docName = "";
            if (data.rc_document && data.rc_document.length > 0) {
                const file = data.rc_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                docName = await uploadFile(file);
            }

            const payload: any = {
                id: location.state.id,
                ...data,
            };

            if (docName) {
                payload.rc_document = docName;
            } else {
                delete payload.rc_document; // Don't send if not updating
            }
            // Remove FileList object
            if (payload.rc_document instanceof FileList) delete payload.rc_document;

            // Auto-determine status: Completed if all mandatory fields present (doc from DB or new upload)
            const hasDocument = docName || !!docPreview;
            payload.status = (
                data.branch_id &&
                data.vehicle_id &&
                data.rto_name?.trim() &&
                data.renewal_date &&
                data.expiry_date &&
                data.amount &&
                hasDocument
            ) ? 'Completed' : 'Pending';

            await apiPost("rc_renewal/update", payload);
            toast.success("RC Renewal updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/rc_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating RC renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit RC Renewal" parent="Renewal" title="Edit RC Renewal" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Renewal Details</H5>
                                <hr />
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Vehicle <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vehicle_id"
                                            control={control}
                                            rules={{ required: "Vehicle is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.vehicle_id}>
                                                        <option value="">Select Vehicle</option>
                                                        {vehicles.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>RTO / Authority Name <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="rto_name"
                                            control={control}
                                            rules={{ required: "Authority Name is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="e.g. RTA Dubai" invalid={!!errors.rto_name} autoComplete="off" />
                                                    <FormFeedback>{errors.rto_name?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Start Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="renewal_date"
                                            control={control}
                                            rules={{ required: "Start Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.renewal_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedExpiryDate && d && new Date(d) > new Date(watchedExpiryDate)) {
                                                                toast.error("Renewal date cannot be after Expiry date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.renewal_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Expiry Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="expiry_date"
                                            control={control}
                                            rules={{ required: "Expiry Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedRenewalDate && d && new Date(d) < new Date(watchedRenewalDate)) {
                                                                toast.error("Expiry date cannot be before Renewal date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Renewal Amount ({config.currency}) <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="amount"
                                            control={control}
                                            rules={{ required: "Amount is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder={`Amount (${config.currency})`} invalid={!!errors.amount} autoComplete="off" />
                                                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>RC Document <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="rc_document"
                                            control={control}
                                            rules={{ required: !docPreview && "RC Document is required" }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                const file = e.target.files[0];
                                                                if (file.size > 2 * 1024 * 1024) {
                                                                    toast.error("File size must be less than 2MB");
                                                                    e.target.value = ""; // Clear the input
                                                                    onChange(null); // Clear form state
                                                                    return;
                                                                }
                                                            }
                                                            onChange(e.target.files);
                                                        }}
                                                        invalid={!!errors.rc_document}
                                                    />
                                                    <FormFeedback>{errors.rc_document?.message}</FormFeedback>

                                                    {docPreview && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={docPreview} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Label>Remarks</Label>
                                        <Controller
                                            name="remarks"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" {...field} placeholder="Any remarks..." autoComplete="off" />
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                {/* Pending warning */}
                                {submissionBlocked && (
                                    <div className="alert alert-warning mt-3 py-2 text-danger">
                                        <strong>⚠ Blocked:</strong> {pendingMessage}
                                    </div>
                                )}

                                {hasPermission('rc-renewal.edit') && (
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
